import { HTMLProps, memo, ReactNode, useMemo, useState } from 'react';
import { Placement } from '@popperjs/core';
import cx from 'classnames';
import { AnimatePresence, motion, Transition, Variants } from 'framer-motion';
import { Modifier, usePopper } from 'react-popper';

import { CloseNewIcon } from 'components/Icon';

import styles from './Tooltip.module.scss';

export type TooltipProps = Pick<HTMLProps<HTMLDivElement>, 'className'> & {
  children(ref: React.Dispatch<React.SetStateAction<HTMLElement | null>>): ReactNode;
  title?: string | ReactNode;
  visible?: boolean;
  placement?: Placement;
  withArrow?: boolean;
  offset?: number;
  onClose?(): void;
};

const variants: Variants = {
  enter: () => ({ opacity: 0 }),
  center: { opacity: 1 },
  exit: () => ({ opacity: 0 })
};

const transition: Transition = {
  duration: 0.3,
  delay: 0.2
};

const Tooltip = ({
  children,
  className,
  title,
  visible = false,
  placement,
  withArrow = false,
  offset = 20,
  onClose
}: TooltipProps): JSX.Element => {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const { styles: popperStyles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: useMemo(() => {
      const result: Modifier<'arrow' | 'offset'>[] = [];

      if (withArrow) {
        result.push({
          name: 'arrow',
          options: { element: arrowElement, padding: 2 }
        });
      }

      result.push({
        name: 'offset',
        options: { offset: [0, offset] }
      });

      return result;
    }, [withArrow, arrowElement, offset])
  });

  return (
    <>
      {children(setReferenceElement)}
      <AnimatePresence initial={false}>
        {visible && (
          <motion.div
            ref={setPopperElement}
            className={cx(styles.container, className, { [styles.closable]: !!onClose })}
            style={popperStyles.popper}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={transition}
            {...attributes.popper}
          >
            <div className={styles.content}>
              <span className={styles.text}>{title}</span>
              {onClose && (
                <button type="button" className={styles.close} onClick={onClose}>
                  <CloseNewIcon />
                </button>
              )}
            </div>
            {withArrow && <div ref={setArrowElement} className={styles.arrow} style={popperStyles.arrow} />}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const memoizedTooltip = memo(Tooltip);

export default Tooltip;
